import React, { useState, useRef, useEffect } from 'react';
import rovquest_logo from 'assets/images/rovquest_logo_primary.png';
import MainOnboard from '../mainOnboard';
import forgot_password from 'assets/icons/forgot_password.svg';
import back from 'assets/icons/back.svg';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from 'config/constant';
import { languageHeaders } from 'config/requestLanguage';
import { useTranslation } from 'react-i18next';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation('login');
  const [verificationCode, setVerificationCode] = useState<string[]>(
    new Array(6).fill('')
  );
  const [errorMessage, setErrorMessage] = useState('');
  const location = useLocation();
  const email = (location.state as { email: string })?.email;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const continueButtonRef = useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!email) {
      navigate('/404');
    }
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [email]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      const otp = verificationCode.join('');
      const response = await axios.post(
        `${API_BASE_URL}/v1/verify_otp/`,
        { email: email, otp: otp },
        // EV-271。表示言語を送り、エラー文の言語をそろえる。
        { headers: languageHeaders() }
      );

      if (response.status === 200) {
        const { token } = response.data;
        navigate('/new-password', { state: { email, token, otp } });
      } else {
        setErrorMessage(t('password-reset.error'));
      }
    } catch (error: any) {
      // RV-370 の続き（2026-10-12 PM 承認の文言 S6）。招待中の運転者は OTP の再設定を使えない。
      setErrorMessage(
        error?.response?.data?.code === 'invitation_pending'
          ? t('error_invitation_pending')
          : t('password-reset.error')
      );
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value;
    if (isNaN(Number(value))) return;

    setErrorMessage('');

    const updatedVerificationCode = [...verificationCode];
    updatedVerificationCode[index] = value.substring(value.length - 1);
    setVerificationCode(updatedVerificationCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value !== '' && index === 5) {
      continueButtonRef.current?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    switch (e.key) {
      case 'Backspace':
        if (!verificationCode[index] && index > 0) {
          inputRefs.current[index - 1]?.focus();
        }
        break;
      case 'Enter':
        continueButtonRef.current?.focus();
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        if (index > 0) {
          const prevInput = inputRefs.current[index - 1];
          prevInput?.focus();
        }
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        if (index < 5) {
          const nextInput = inputRefs.current[index + 1];
          nextInput?.focus();
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className="flex min-h-screen">
      <img
        src={rovquest_logo}
        alt="Rovquest Logo"
        className="fixed top-8 left-6 w-64 h-16"
      />
      <div className="flex-1 flex items-center">
        <div className="flex-col justify-start items-center gap-12 inline-flex w-full mx-20">
          <div className="flex-col justify-start items-center gap-6 inline-flex">
            <img src={forgot_password} alt="" />
            <div className="w-full flex-col justify-start items-center gap-2 inline-flex">
              <div className="text-black text-[33px] font-bold font-['Noto Sans'] leading-10">
                {t('password-reset.title')}
              </div>
              <div className="self-stretch text-center text-neutral/100 text-subtitle-1 font-medium">
                {t('password-reset.sub-title')}{' '}
                <span className="text-neutral/300">{email}</span>
              </div>
            </div>
          </div>
          <form
            onSubmit={handleSubmit}
            className="w-full flex-col justify-start items-center gap-12 inline-flex"
          >
            <div className="flex-col justify-start items-center gap-1 w-full">
              <label
                htmlFor="Verification code"
                className="block mb-2 text-sm font-regular text-neutral/100"
              >
                {t('password-reset.verification')}
              </label>
              <div className="flex gap-4 lg:gap-8">
                {[...Array(6)].map((_, index) => (
                  <input
                    key={index}
                    type="text"
                    id={`code${index + 1}`}
                    ref={(el) => (inputRefs.current[index] = el)}
                    className="w-1/6 aspect-square text-h5 text-center border-2 border-neutral/50 text-neutral/30 focus:outline-none focus:border-neutral/75 focus:ring-4 focus:ring-neutral/50 ring-offset rounded-md"
                    maxLength={1}
                    required
                    value={verificationCode[index]}
                    onChange={(e) => handleInputChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                  />
                ))}
              </div>
            </div>
            <div className="flex flex-col w-full">
              {errorMessage && (
                <div className="bg-error/[0.1] text-error px-4 py-3 rounded-lg relative mb-4">
                  <span className="block sm:inline">{errorMessage}</span>
                </div>
              )}
              <button
                type="submit"
                className="flex w-full h-16 rounded-lg justify-center items-center bg-primary/300 text-title text-white"
              >
                {t('password-reset.button')}
              </button>
            </div>
          </form>

          <a className="flex items-center gap-2" href="/login">
            <img src={back} alt="" />
            <div className="text-subtitle-1 text-neutral/300">
              {t('password-reset.back')}
            </div>
          </a>
        </div>
      </div>
      <MainOnboard />
    </div>
  );
};

export default ResetPassword;
